import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { PatronEditBooking } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/types";
import { JoinBooking } from "@/screens/JoinBooking/types";
import { DropdownSelectOption } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { useGetBundledRateOptionsQuery } from "./__generated__/useGetBundledRateOptionsQuery.graphql";
import { TransportationTypeOption } from "./types/transportationTypeOption";
import { useGetBundledRateOptionsQueryString } from "./useGetBundledRateOptions";

type HoleCount = "EIGHTEEN" | "NINE" | "NONE";

export const useGetSingleBundleRateOption = (
  round: PatronEditBooking.Round | JoinBooking.CustomerRound | null,
  currentHoleCount: HoleCount,
  queryRef: PreloadedQuery<useGetBundledRateOptionsQuery>,
  selectedTransportationTypeId: string,
  transportationTypesOverride?: TransportationTypeOption[]
) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = usePreloadedQuery<useGetBundledRateOptionsQuery>(
    useGetBundledRateOptionsQueryString,
    queryRef
  );
  const isPrivateClub =
    data?.slot?.agenda?.facility?.club?.supportsPrivate ?? false;

  const currentRate = round && "roundRate" in round ? round.roundRate : null;
  const queryTransportationTypes =
    data?.slot?.transportationTypes?.flatMap((tt) => (tt ? [tt] : [])) ?? [];
  const transportationTypes =
    transportationTypesOverride && transportationTypesOverride.length > 0
      ? transportationTypesOverride
      : queryTransportationTypes;

  // Default to first available transportation type if none is selected
  const effectiveTransportationTypeId =
    selectedTransportationTypeId ||
    (transportationTypes.length > 0 ? transportationTypes[0].id : undefined);

  const currentTransportationType = transportationTypes.find(
    (tt) =>
      tt.id === (selectedTransportationTypeId || effectiveTransportationTypeId)
  );
  const currentTransportationIcon = GetTransportIconFromCode(
    currentTransportationType?.customCode ||
      currentTransportationType?.code ||
      "",
    "mainTiny"
  );
  const ratePriceForHoleCount =
    (currentHoleCount === "EIGHTEEN"
      ? currentRate?.eighteenHolePrice
      : currentRate?.nineHolePrice) || 0;

  const transportationTypePriceForHoleCount =
    (currentHoleCount === "EIGHTEEN"
      ? currentTransportationType?.eighteenHolePrice
      : currentTransportationType?.nineHolePrice) || 0;

  const totalCost = ratePriceForHoleCount + transportationTypePriceForHoleCount;
  const totalCostLabel =
    !isPrivateClub && totalCost
      ? `${currencySymbol}${ConvertCentsToDollars(totalCost)}`
      : "";

  const singleOption: DropdownSelectOption<string> = {
    value: `${currentRate?.id}.${effectiveTransportationTypeId}`,
    label: currentRate?.name ?? "",
    subLabel: currentTransportationType?.name ?? "",
    leftElement: currentTransportationIcon
      ? currentTransportationIcon({})
      : undefined,
    triggerElement: (
      <HStack
        gap={3}
        alignItems="center"
        flex={1}
        overflow="hidden"
        justifyContent={"space-between"}
      >
        {currentTransportationIcon ? currentTransportationIcon({}) : undefined}

        <HStack gap={2} flex={1}>
          <Text variant="mainSmallSemiBold" numberOfLines={1}>
            {currentRate?.name} - {currentTransportationType?.name}
          </Text>

          <Text variant="mainBaseMedium" color={"text-secondary"}>
            {totalCostLabel}
          </Text>
        </HStack>
      </HStack>
    ),
  };

  return singleOption;
};
